import { supabase } from '@/integrations/supabase/client';
import type { IPublicAnalyticsPort } from '../ports/publicAnalytics.port';
import type {
  PublicCampaignMetadata,
  PublicCampaignSummary,
  PublicDeliverableAggregate,
  PublicCampaignBranding,
} from '../types/sharedAnalytics.types';
import type { CampaignPerformanceDataPoint, PlatformDistributionItem } from '../types/analytics.types';
import type { TopPost } from '@/domains/content';

type RpcClient = {
  rpc: <T>(fn: string, args?: Record<string, unknown>) => Promise<{ data: T | null; error: Error | null }>;
};

const rpcClient = supabase as unknown as RpcClient;

interface RpcPlatformRow {
  platform: string;
  pct: number;
}

interface RpcTopPostRow {
  id: string;
  post_url: string | null;
  content_type: string;
  platform: string;
  influencer_name: string | null;
  influencer_avatar: string | null;
  thumbnail_url: string | null;
  views: number;
  reach: number;
  likes: number;
  comments: number;
  shares: number;
  engagement: number;
  engagement_rate: number;
  er_followers?: number | null;
  er_views?: number | null;
  published_on: string | null;
}

const PLATFORM_CONFIG: Record<string, { label: string; color: string }> = {
  instagram: { label: 'Instagram', color: '#E91E63' },
  youtube: { label: 'YouTube', color: '#FF0000' },
  tiktok: { label: 'TikTok', color: '#00F2EA' },
  twitter: { label: 'Twitter', color: '#1DA1F2' },
  linkedin: { label: 'LinkedIn', color: '#0A66C2' },
};

const first = <T>(data: T[] | T | null): T | null => {
  if (!data) return null;
  return Array.isArray(data) ? data[0] ?? null : data;
};

export class PublicAnalyticsRepository implements IPublicAnalyticsPort {
  async getSharedCampaignMetadata(token: string): Promise<PublicCampaignMetadata | null> {
    const { data, error } = await rpcClient.rpc<PublicCampaignMetadata[]>(
      'validate_share_token',
      { p_token: token }
    );
    if (error) {
      console.error('Error fetching public campaign metadata:', error);
      throw error;
    }
    const metadata = first(data);
    if (!metadata) {
      throw new Error('Share link is invalid, expired, or revoked.');
    }
    return metadata;
  }

  async getSharedCampaignBranding(token: string): Promise<PublicCampaignBranding | null> {
    const { data, error } = await rpcClient.rpc<PublicCampaignBranding[]>(
      'get_public_campaign_branding',
      { p_token: token }
    );
    if (error) {
      console.error('Error fetching public campaign branding:', error);
      return null;
    }
    return first(data);
  }

  async getSharedCampaignPerformanceTimeSeries(
    token: string
  ): Promise<CampaignPerformanceDataPoint[]> {
    const { data, error } = await rpcClient.rpc<Array<{
      month_label: string;
      reach: number;
      engagement: number;
      views: number;
      impressions: number;
    }>>('get_public_campaign_performance_timeseries', {
      p_token: token,
    });
    if (error) {
      console.error('Error fetching public performance time series:', error);
      throw error;
    }
    return (data ?? []).map((row) => ({
      name: row.month_label,
      reach: Number(row.reach),
      engagement: Number(row.engagement),
      views: Number(row.views),
      impressions: Number(row.impressions),
    }));
  }

  async getSharedCampaignSummary(token: string): Promise<PublicCampaignSummary | null> {
    const { data, error } = await rpcClient.rpc<PublicCampaignSummary>(
      'get_public_campaign_summary',
      { p_token: token }
    );
    if (error) {
      console.error('Error fetching public campaign summary:', error);
      throw error;
    }
    return data ?? { cpv: null, cpe: null };
  }

  async getSharedDeliverableAggregates(token: string): Promise<PublicDeliverableAggregate[]> {
    const { data, error } = await rpcClient.rpc<PublicDeliverableAggregate[]>(
      'get_public_deliverable_aggregates',
      { p_token: token }
    );
    if (error) {
      console.error('Error fetching public deliverable aggregates:', error);
      throw error;
    }
    return data ?? [];
  }

  async getSharedPlatformDistribution(token: string): Promise<PlatformDistributionItem[]> {
    const { data, error } = await rpcClient.rpc<RpcPlatformRow[]>(
      'get_public_platform_distribution',
      { p_token: token }
    );
    if (error) {
      console.error('Error fetching public platform distribution:', error);
      throw error;
    }

    return (data ?? [])
      .map((row) => {
        const config = PLATFORM_CONFIG[row.platform?.toLowerCase?.() || ''];
        if (!config) return null;
        return {
          name: config.label,
          value: row.pct,
          color: config.color,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }

  async getSharedTopPerformingPosts(token: string): Promise<TopPost[]> {
    const { data, error } = await rpcClient.rpc<RpcTopPostRow[]>(
      'get_public_top_performing_posts',
      { p_token: token }
    );
    if (error) {
      console.error('Error fetching public top performing posts:', error);
      throw error;
    }

    return (data ?? []).map((row) => ({
      id: row.id,
      postUrl: row.post_url,
      contentType: row.content_type,
      platform: row.platform,
      influencerName: row.influencer_name ?? 'Creator',
      influencerAvatar: row.influencer_avatar ?? null,
      thumbnailUrl: row.thumbnail_url ?? null,
      views: Number(row.views || 0),
      reach: Number(row.reach || 0),
      likes: Number(row.likes || 0),
      comments: Number(row.comments || 0),
      shares: Number(row.shares || 0),
      engagement: Number(row.engagement || 0),
      engagementRate: Number(row.engagement_rate || 0),
      erFollowers: row.er_followers != null ? Number(row.er_followers) : null,
      erViews: row.er_views != null ? Number(row.er_views) : null,
      publishedOn: row.published_on ?? null,
    }));
  }
}

export const publicAnalyticsRepository = new PublicAnalyticsRepository();
